import PropTypes from 'prop-types'
import React from 'react'
import { TransitionGroup } from 'react-transition-group'
import { cn } from 'ethcss'
import styles from './animation.jcss'
import { transitions } from 'blocks.app/config'
import {
    AnimationDefaultProps,
    AnimationDoneCallback,
    AnimationInnerProps,
    AnimationInnerState,
    AnimationPhase,
    AnimationProps,
    AnimationWrapperProps,
    LegacyTransitionGroupProps,
} from './animation.types'

const LegacyTransitionGroup = (TransitionGroup as unknown) as React.ComponentType<LegacyTransitionGroupProps>

const getTransition = (p_: AnimationInnerProps): number => transitions[p_.transition] || transitions.normal
const getTransitionName = (p_: AnimationInnerProps): string => `${p_.transitionName}_${p_.transition}`
const getClassName = (p_: AnimationInnerProps, prefix: string): string => `${getTransitionName(p_)}-${prefix}`

class AnimationInner extends React.Component<AnimationInnerProps, AnimationInnerState> {
    state: AnimationInnerState = {
        className: '',
    }
    startAnimateTimer: ReturnType<typeof setTimeout> | null = null
    endAnimateTimer: ReturnType<typeof setTimeout> | null = null

    componentWillEnter(cb: AnimationDoneCallback) {
        this.animate(cb, 'enter')
    }
    componentWillLeave(cb: AnimationDoneCallback) {
        this.animate(cb, 'leave')
    }
    componentWillUnmount() {
        if (this.startAnimateTimer) {
            clearTimeout(this.startAnimateTimer)
        }
        if (this.endAnimateTimer) {
            clearTimeout(this.endAnimateTimer)
        }
    }
    animate = (cb: AnimationDoneCallback, prefix: AnimationPhase) => {
        const p_ = this.props

        const transition = getTransition(p_)

        this.setState({ className: getClassName(p_, prefix) }, () => {
            this.startAnimateTimer = setTimeout(() => {
                this.setState({ className: cn(this.state.className, getClassName(p_, `${prefix}-active`)) }, () => {
                    this.endAnimateTimer = setTimeout(() => this.endAnimate(cb), transition)
                })
            }, 0)
        })
    }
    endAnimate = (cb: AnimationDoneCallback) => {
        this.setState({ className: '' }, cb)
    }

    extendChildren(children: React.ReactNode, className: string): React.ReactNode {
        return React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
                return child
            }
            const element = (child as unknown) as React.ReactElement<{ className?: string }>

            return React.cloneElement(element, {
                className: cn(element.props.className, className),
            })
        })
    }
    render() {
        const p_ = this.props
        const s_ = this.state

        return this.extendChildren(p_.children, s_.className)
    }
}

class AnimationWrapper extends React.Component<AnimationWrapperProps> {
    render() {
        const { activeClassName, isActive, children, className, ...divProps } = this.props
        const active = isActive(React.Children.count(children))

        return (
            <div {...divProps} className={cn(className, active && activeClassName)}>
                {children}
            </div>
        )
    }
}

class Animation extends React.Component<AnimationProps> {
    static propTypes = {
        transition: PropTypes.string,
        className: PropTypes.string,
        activeClassName: PropTypes.string,
        isActive: PropTypes.func,
        children: PropTypes.node,
        transitionName: PropTypes.string,
        style: PropTypes.object,
    }
    static defaultProps: AnimationDefaultProps = {
        transitionName: 'fade',
        transition: 'normal',
        isActive: () => false,
    }
    render() {
        const p_ = this.props
        const childKey = React.isValidElement(p_.children) ? p_.children.key || 'animation' : 'animation'

        return (
            <LegacyTransitionGroup
                component={AnimationWrapper}
                className={cn(styles.wrapper, p_.className)}
                activeClassName={p_.activeClassName}
                isActive={p_.isActive}
                style={p_.style}
            >
                {!!p_.children ? (
                    <AnimationInner key={childKey} transition={p_.transition} transitionName={p_.transitionName}>
                        {p_.children}
                    </AnimationInner>
                ) : null}
            </LegacyTransitionGroup>
        )
    }
}

export default Animation
